import React, { useEffect, useState, useRef } from 'react';
import { useStaticQuery, graphql } from 'gatsby';

import { CoverWrapper } from './Cover';
import { VideoButton } from '../Page';
import VideoPlayerOptimized from '../Video/VideoPlayerOptimized';

const CoverVideoOptimized = () => {
  const data = useStaticQuery(graphql`
    query {
      poster: file(name: {eq: "poster-640w"}, ext: {eq: ".webp"}) {
        publicURL
      }
      posterLarge: file(name: {eq: "poster"}, ext: {eq: ".webp"}) {
        publicURL
      }
    }
  `);

  const [loadVideo, setLoadVideo] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [shouldLoadPoster, setShouldLoadPoster] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const videoRef = useRef(null);

  // Screen size detection
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const matchMedia = window.matchMedia('(min-width: 640px)');
    const mobileMedia = window.matchMedia('(max-width: 768px)');
    
    setLoadVideo(matchMedia.matches);
    setIsMobile(mobileMedia.matches);

    const handleDesktopChange = (e) => setLoadVideo(e.matches);
    const handleMobileChange = (e) => setIsMobile(e.matches);

    matchMedia.addEventListener('change', handleDesktopChange);
    mobileMedia.addEventListener('change', handleMobileChange);

    return () => {
      matchMedia.removeEventListener('change', handleDesktopChange);
      mobileMedia.removeEventListener('change', handleMobileChange);
    };
  }, []);

  // Intersection observer for lazy loading
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          // Load poster image when component becomes visible
          setShouldLoadPoster(true);
          observer.disconnect();
        }
      },
      {
        // Load when video is closer on mobile to improve performance
        rootMargin: isMobile ? '100px 0px' : '200px 0px',
        threshold: 0
      }
    );

    if (videoRef.current) {
      observer.observe(videoRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <CoverWrapper>
      <div className="sm:pt-0" ref={videoRef}>
        <div className="aspect-w-16 aspect-h-9">
          {loadVideo && isVisible ? (
            <VideoPlayerOptimized 
              poster={data.posterLarge?.publicURL || null}
              lazy={true}
            />
          ) : (
            <>
              {shouldLoadPoster && data.poster?.publicURL && (
                <>
                  <div className="text-white z-10 flex justify-center items-center bg-[rgba(0,0,0,0.25)]">
                    <div className="absolute">
                      <VideoButton>
                        Watch Video
                      </VideoButton>
                    </div>
                  </div>
                  <img 
                    src={data.poster.publicURL} 
                    alt="The smiling faces of two entrepreneurs (screen capture from Abler Nordic video)." 
                    loading="eager"
                    className="object-cover h-full sm:h-auto"
                    decoding="async"
                    fetchPriority="high"
                    width="640"
                    height="360"
                  />
                </>
              )}
              {!shouldLoadPoster && (
                // Placeholder to maintain layout until image loads
                <div className="bg-gray-200 animate-pulse w-full h-full flex items-center justify-center">
                  <div className="text-gray-400 text-sm">Loading video...</div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </CoverWrapper>
  );
};

export default CoverVideoOptimized;